// PWA utilities for installation and offline functionality

export interface PWAInstallPrompt {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

class PWAManager {
  private deferredPrompt: PWAInstallPrompt | null = null;
  private isInstalled = false;
  private installCallbacks: ((canInstall: boolean) => void)[] = [];

  constructor() {
    this.initialize();
  }

  private initialize() {
    // Check if already installed
    this.checkInstallationStatus();

    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('[PWA] Install prompt available');
      e.preventDefault();
      this.deferredPrompt = e as any;
      this.notifyInstallCallbacks(true);
    });

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] App installed');
      this.isInstalled = true;
      this.deferredPrompt = null;
      this.notifyInstallCallbacks(false);
    });

    // Check if launched as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('[PWA] Launched as standalone app');
      this.isInstalled = true;
    }
  }

  private checkInstallationStatus() {
    // Check if running as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isInWebApk = 'matchMedia' in window && window.matchMedia('(display-mode: standalone)').matches;
    const isIOSPWA = (window.navigator as any).standalone === true;

    this.isInstalled = isStandalone || isInWebApk || isIOSPWA;
  }

  private notifyInstallCallbacks(canInstall: boolean) {
    this.installCallbacks.forEach(callback => callback(canInstall));
  }

  // Check if app can be installed
  public canInstall(): boolean {
    return this.deferredPrompt !== null && !this.isInstalled;
  }

  // Check if app is already installed
  public getIsInstalled(): boolean {
    return this.isInstalled;
  }

  // Show install prompt
  public async showInstallPrompt(): Promise<boolean> {
    if (!this.deferredPrompt) {
      throw new Error('Install prompt not available');
    }

    try {
      await this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      
      console.log('[PWA] Install prompt result:', outcome);
      
      if (outcome === 'accepted') {
        this.deferredPrompt = null;
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('[PWA] Install prompt error:', error);
      throw error;
    }
  }

  // Register callback for install availability changes
  public onInstallAvailable(callback: (canInstall: boolean) => void) {
    this.installCallbacks.push(callback);
    
    // Call immediately with current state
    callback(this.canInstall());
    
    // Return unsubscribe function
    return () => {
      const index = this.installCallbacks.indexOf(callback);
      if (index > -1) {
        this.installCallbacks.splice(index, 1);
      }
    };
  }

  // Get install instructions for iOS
  public getIOSInstallInstructions(): string[] {
    return [
      '1. Tap the Share button at the bottom of the screen',
      '2. Scroll down and tap "Add to Home Screen"',
      '3. Tap "Add" to install the app',
    ];
  }

  // Check if iOS
  public isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  // Check if can show iOS install instructions
  public shouldShowIOSInstructions(): boolean {
    return this.isIOS() && !this.isInstalled && !this.canInstall();
  }
}

// Service Worker registration
export class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;
  private updateCallbacks: (() => void)[] = [];

  public async register(): Promise<ServiceWorkerRegistration | null> {
    if ('serviceWorker' in navigator) {
      try {
        console.log('[SW] Registering service worker');
        
        this.registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        });

        console.log('[SW] Service worker registered:', this.registration.scope);

        // Listen for updates
        this.registration.addEventListener('updatefound', () => {
          console.log('[SW] New service worker found');
          const newWorker = this.registration?.installing;
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('[SW] New version available');
                this.notifyUpdateCallbacks();
              }
            });
          }
        });

        // Listen for controlling service worker change
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          console.log('[SW] Controller changed, reloading page');
          window.location.reload();
        });

        return this.registration;
      } catch (error) {
        console.error('[SW] Service worker registration failed:', error);
        return null;
      }
    } else {
      console.warn('[SW] Service workers not supported');
      return null;
    }
  }

  private notifyUpdateCallbacks() {
    this.updateCallbacks.forEach(callback => callback());
  }

  // Update service worker
  public async update(): Promise<void> {
    if (this.registration) {
      await this.registration.update();
    }
  }

  // Skip waiting and activate new service worker
  public skipWaiting(): void {
    if (this.registration?.waiting) {
      this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  }

  // Register callback for updates
  public onUpdateAvailable(callback: () => void) {
    this.updateCallbacks.push(callback);
    
    return () => {
      const index = this.updateCallbacks.indexOf(callback);
      if (index > -1) {
        this.updateCallbacks.splice(index, 1);
      }
    };
  }
}

// Offline storage manager
export class OfflineStorageManager {
  private dbName = 'CheckInOfflineDB';
  private version = 1;
  private db: IDBDatabase | null = null;

  public async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create stores for offline data
        if (!db.objectStoreNames.contains('pendingOperations')) {
          const store = db.createObjectStore('pendingOperations', { keyPath: 'id', autoIncrement: true });
          store.createIndex('timestamp', 'timestamp');
          store.createIndex('type', 'type');
        }
        
        if (!db.objectStoreNames.contains('cachedData')) {
          const store = db.createObjectStore('cachedData', { keyPath: 'key' });
          store.createIndex('timestamp', 'timestamp');
        }
      };
    });
  }

  // Store pending operation for background sync
  public async storePendingOperation(operation: any): Promise<void> {
    if (!this.db) await this.initialize();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['pendingOperations'], 'readwrite');
      const store = transaction.objectStore('pendingOperations');
      
      const request = store.add({
        ...operation,
        timestamp: Date.now(),
      });
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Get pending operations
  public async getPendingOperations(): Promise<any[]> {
    if (!this.db) await this.initialize();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['pendingOperations'], 'readonly');
      const store = transaction.objectStore('pendingOperations');
      
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Remove pending operation
  public async removePendingOperation(id: number): Promise<void> {
    if (!this.db) await this.initialize();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['pendingOperations'], 'readwrite');
      const store = transaction.objectStore('pendingOperations');
      
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

// Export singleton instances
export const pwaManager = new PWAManager();
export const serviceWorkerManager = new ServiceWorkerManager();
export const offlineStorageManager = new OfflineStorageManager();